import { useState, useEffect } from 'react';
import { FilterTextField } from './FilterTextField';
import { useDebounce } from './useDebounce';
import { SelectV2 } from '../select-v2';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

export enum ComparisonOperator {
  STARTS_WITH = 'startsWith',
  ENDS_WITH = 'endsWith',
  CONTAINS = 'contains',
  EQUALS = 'equals',
  ANY = 'any',
}

export enum ExistenceOperator {
  DOES_NOT_EXIST = 'doesNotExist',
}

const tagValueMenuOptions = (t: TFunction<'designSystem'>) => [
  { key: ComparisonOperator.EQUALS, label: t('equals') },
  { key: ComparisonOperator.STARTS_WITH, label: t('startsWith') },
  { key: ComparisonOperator.ENDS_WITH, label: t('endsWith') },
  { key: ComparisonOperator.CONTAINS, label: t('contains') },
  { key: ComparisonOperator.ANY, label: t('anyValue') },
  { key: ExistenceOperator.DOES_NOT_EXIST, label: t('absent') },
];

export type TagProps = {
  key?: string;
  value?: string;
  operator?: ComparisonOperator | ExistenceOperator;
};

interface TagFilterProps {
  value: TagProps;
  setValue: (value: TagProps) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  editable?: boolean;
  wrapperClassName?: string;
  dataTestId?: string;
}

export const TagFilter: React.FC<TagFilterProps> = ({
  value: filterValue,
  setValue,
  keyPlaceholder = 'Tag key',
  valuePlaceholder = 'Tag value',
  editable = true,
  wrapperClassName = '',
  dataTestId,
}) => {
  const { t } = useTranslation('designSystem');
  const [localKey, setLocalKey] = useState(filterValue.key);
  const [localValue, setLocalValue] = useState(filterValue.value);
  const debouncedKey = useDebounce(localKey);
  const debouncedValue = useDebounce(localValue);
  const textContainerClassName =
    'flex h-full w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 placeholder-gray-400 dark:placeholder-gray-500 justify-center items-center font-normal focus:border-gray-300 dark:focus:border-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-500 dark:focus:ring-gray-400';

  useEffect(() => {
    const trimmedKey = debouncedKey?.trim();
    if (trimmedKey !== undefined && trimmedKey !== (filterValue.key ?? '')) {
      setValue({ ...filterValue, key: trimmedKey });
    }
  }, [debouncedKey]);

  useEffect(() => {
    const trimmedValue = debouncedValue?.trim();
    if (trimmedValue !== undefined && trimmedValue !== (filterValue.value ?? '')) {
      setValue({ ...filterValue, value: trimmedValue });
    }
  }, [debouncedValue]);

  useEffect(() => {
    setLocalKey(filterValue.key ?? '');
  }, [filterValue.key]);

  useEffect(() => {
    setLocalValue(filterValue.value ?? '');
  }, [filterValue.value]);

  const handleOperatorChange = (newOperator?: string) => {
    if (
      Object.values({ ...ComparisonOperator, ...ExistenceOperator }).includes(
        newOperator as ComparisonOperator | ExistenceOperator,
      )
    ) {
      if (
        newOperator === ExistenceOperator.DOES_NOT_EXIST ||
        newOperator === ComparisonOperator.ANY
      ) {
        setLocalValue('');
        setValue({
          ...filterValue,
          operator: newOperator,
          value: undefined,
        });
      } else {
        setValue({
          ...filterValue,
          operator: newOperator as ComparisonOperator,
        });
      }
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${wrapperClassName}`}>
      <div className="flex h-full flex-grow gap-2">
        <div
          className={`${textContainerClassName}`}
          data-testid={`${dataTestId}-key`}
        >
          <FilterTextField
            value={localKey}
            setValue={setLocalKey}
            placeholder={keyPlaceholder}
            showClear
          />
        </div>
        <div
          className="flex h-full w-[12rem]"
          data-testid={`${dataTestId}-operator`}
        >
          <SelectV2
            wrapperClassName="w-full h-full"
            optionsClassName="absolute bottom-[100%]"
            selectClass="py-[0.625rem]"
            listboxContainerClassName="relative mt-0"
            options={tagValueMenuOptions(t)}
            hideCloseIcon={true}
            disabled={!editable}
            value={filterValue.operator || ComparisonOperator.ANY}
            setValue={handleOperatorChange}
          />
        </div>
      </div>

      {filterValue.operator &&
        filterValue.operator !== ExistenceOperator.DOES_NOT_EXIST &&
        filterValue.operator !== ComparisonOperator.ANY && (
          <div
            className={`${textContainerClassName}`}
            data-testid={`${dataTestId}-value`}
          >
            <FilterTextField
              value={localValue}
              setValue={setLocalValue}
              placeholder={valuePlaceholder}
              showClear
            />
          </div>
        )}
    </div>
  );
};
